import { useQuery } from '@tanstack/react-query';
import { Calendar } from 'lucide-react';

import { browseQueries } from '@/lib/queries/browse';

/** Compact "Tue, 8:00 PM" for the scheduled slot (drops :00 when on the hour). */
function scheduledLabel(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    weekday: 'short',
    hour: 'numeric',
    minute: new Date(iso).getMinutes() ? '2-digit' : undefined,
  });
}

function PanelHeading({ children }: { children: string }) {
  return (
    <p className="font-sans text-xs font-medium uppercase tracking-[0.12em] text-video-text-faint">
      {children}
    </p>
  );
}

/** Host avatar in the liveness ring, or initials when there's no avatar. */
function HostAvatar({
  name,
  avatarUrl,
  ring,
}: {
  name: string;
  avatarUrl: string | null;
  ring: string;
}) {
  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt=""
        className={`h-7 w-7 shrink-0 rounded-full bg-video-card object-cover ${ring}`}
      />
    );
  }
  return (
    <span
      aria-hidden
      className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-video-card font-sans text-xs text-video-text-muted ${ring}`}
    >
      {name.trim().charAt(0).toUpperCase() || '•'}
    </span>
  );
}

/**
 * Sidebar live panel: currently-live rooms (by started_at desc), then
 * recently-live, then the next scheduled stream. `liveNow`/`recentlyLive` are
 * data-backed in Phase G G5; `nextScheduled` stays stubbed (no scheduling in v1)
 * so its slot stays hidden. The ring weights encode liveness (heavy = live, thin
 * = recently live, dashed = scheduled). Live rows link to `/live/$slug`; recent
 * rows are non-links until replay/VOD lands (G6).
 */
export function LiveNowPanel() {
  const { data: live = [] } = useQuery(browseQueries.liveNow());
  const { data: recent = [] } = useQuery(browseQueries.recentlyLive());
  const { data: next = null } = useQuery(browseQueries.nextScheduled());

  return (
    <div className="space-y-4">
      <PanelHeading>Live now</PanelHeading>
      {live.length === 0 ? (
        <p className="font-sans text-xs text-video-text-faint">No live streams right now.</p>
      ) : (
        <ul className="space-y-2">
          {live.map((s) => (
            <li key={s.id}>
              <a href={s.watchHref} className="group flex items-center gap-2">
                <HostAvatar
                  name={s.creatorName}
                  avatarUrl={s.creatorAvatarUrl}
                  ring="ring-2 ring-oxblood-live"
                />
                <span className="min-w-0">
                  <span className="block truncate font-sans text-sm text-video-text group-hover:underline">
                    {s.title}
                  </span>
                  <span className="font-sans text-xs font-medium text-oxblood-live">
                    {'●'} Live
                  </span>
                </span>
              </a>
            </li>
          ))}
        </ul>
      )}

      {recent.length > 0 && (
        <ul className="space-y-2 border-t border-video-line pt-3">
          {recent.map((r) => (
            <li key={r.id} className="flex items-center gap-2">
              <HostAvatar
                name={r.creatorName}
                avatarUrl={r.creatorAvatarUrl}
                ring="ring-1 ring-video-line-strong"
              />
              <span className="block min-w-0 truncate font-sans text-sm text-video-text-muted">
                {r.title}
              </span>
            </li>
          ))}
        </ul>
      )}

      {next && (
        <div className="flex items-center gap-2 border-t border-video-line pt-3">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-dashed border-video-line-strong text-video-text-faint">
            <Calendar className="h-3.5 w-3.5" />
          </span>
          <span className="block min-w-0">
            <span className="block truncate font-sans text-sm text-video-text-muted">
              {next.title}
            </span>
            <span className="font-sans text-xs text-video-text-faint">
              {scheduledLabel(next.scheduledFor)}
            </span>
          </span>
        </div>
      )}
    </div>
  );
}
